import React from 'react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react';
import { LockInfo } from 'src/models/common/LockInfo';
import { isLockedByOther } from './editLockUtils';
import { DEFAULT_LOCK_MESSAGES, Action } from './constants';
import CONSTANTS from 'src/utils/constants';

export interface EditLockButtonBarProps {
    viewMode?: string;
    lockInfo?: LockInfo | null;
    metadataLoaded?: boolean;
    currentUser?: string;
    actionInProgress: Action | null;
    onEdit: () => void;
    onSave: () => void;
    onConfirm: () => void;
    onRevert: () => void;
    // Optional caller-supplied gate for the Edit button, independent of lock ownership.
    // Used when the entity is in a state where acquiring the lock is not allowed even though
    // no one else holds it (e.g. a calculation Pending Review). Defaults to not-disabled so
    // existing callers (MDA, TP Allocation) are unaffected.
    editDisabled?: boolean;
    editDisabledReason?: string;
}

export function EditLockButtonBar(props: EditLockButtonBarProps) {
    const { viewMode, lockInfo, metadataLoaded = true, currentUser, actionInProgress, onEdit, onSave, onConfirm, onRevert, editDisabled = false, editDisabledReason } = props;
    const lockedByOther = isLockedByOther(lockInfo, currentUser);

    if (!metadataLoaded) {
        return null;
    }

    if (actionInProgress === Action.EDIT) {
        return (
            <Button variant="primary" loading={true} disabled={true}>
                Edit
            </Button>
        );
    }

    if (actionInProgress === Action.CONFIRM || actionInProgress === Action.REVERT || actionInProgress === Action.SAVE) {
        return (
            <SpaceBetween direction="horizontal" size="s">
                <Button variant="normal" loading={actionInProgress === Action.SAVE} disabled={true}>
                    Save
                </Button>
                <Button variant="normal" loading={actionInProgress === Action.REVERT} disabled={true}>
                    Revert
                </Button>
                <Button variant="primary" loading={actionInProgress === Action.CONFIRM} disabled={true}>
                    Confirm
                </Button>
            </SpaceBetween>
        );
    }

    if (viewMode === CONSTANTS.VIEW_MODE.EDITABLE && !lockedByOther) {
        return (
            <SpaceBetween direction="horizontal" size="s">
                <Button variant="normal" onClick={onSave}>Save</Button>
                <Button variant="normal" onClick={onRevert}>Revert</Button>
                <Button variant="primary" onClick={onConfirm}>Confirm</Button>
            </SpaceBetween>
        );
    }

    return (
        <Button
            variant="primary"
            disabled={lockedByOther || editDisabled}
            disabledReason={
                lockedByOther
                    ? DEFAULT_LOCK_MESSAGES.lockedByOther(lockInfo?.lockedBy || 'another user')
                    : editDisabled
                        ? editDisabledReason
                        : undefined
            }
            onClick={onEdit}
        >
            Edit
        </Button>
    );
}

export default EditLockButtonBar;
